import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import { normalizeEther, normalizeNumber } from '../../helpers/digits';
import useGetCollection from '../../hooks/useGetCollection';
import useGetUSDPriceOfToken from '../../hooks/useGetUSDPriceOfToken';
import { getCdnUrl } from '../../lib/cdnUrl';
import { setModal } from '../../redux/counterSlice';
import { ChainIcon } from '../Icon';
import {
  getCoinGeckoId,
  getCoinGeckoWrappedTokenId,
  getTokenName,
} from '../../helpers/getChainName';
import axios from '../../lib/axios';
import { constants } from '../../helpers/constants';
import useGetItemsByCollectionAddress from '../../hooks/useGetItemsByCollectionAddress';
import { useMarketPlaceContract, useTokenContract } from '../../helpers/setMarketPlaceAbi';
import { ConnectButtonWrapper } from '../Web3WalletProvider';
import { approveERC20 } from '../../helpers/blockchainOperations';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { ShowUser } from '../userImage';
import { getEnd } from '../../helpers/getEnd';
import { ImageFixedAO } from '../Image_fixed_aspect_ratio';
import { ClickAwayListener, Tooltip, useMediaQuery } from '@mui/material';
import { getSupportedChain } from '../../helpers/getSupportedChain';
import PropTypes from 'prop-types';
import { offerShape } from '../../helpers/propTypes';

export const useOrderStatusEffect = (marketPlaceContract, offer) => {
  const [signatureStillActive, setSignatureStillActive] = useState(false);

  useEffect(() => {
    (async () => {
      if (marketPlaceContract && offer?.signature) {
        const data = await marketPlaceContract?.read?.orderStatus?.([offer?.signature]);
        setSignatureStillActive(data === 0);
      }
    })();
    return () => {};
  }, [marketPlaceContract]);

  return signatureStillActive;
};

const GlobalOfferItem = ({ offer }) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:768px)');
  const chain = getSupportedChain(offer?.chain);
  const usdPrice = Object.values(
    useGetUSDPriceOfToken(getCoinGeckoId(chain.hexId), 1)?.data || {},
  )?.[0]?.usd;
  const collection = useGetCollection(offer?.collectionAddress).data;
  const [traitFilterCount, setTraitFilterCount] = useState(null);
  const marketPlaceContract = useMarketPlaceContract();
  const tokenContract = useTokenContract(chain.id);
  const signatureStillActive = useOrderStatusEffect(marketPlaceContract, offer);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { address, isConnected } = useAccount();

  const refreshTraitFilterCount = async () => {
    if (
      !offer.traitFilter ||
      !offer.hasEnoughBalance ||
      !offer.isApprovedEnough ||
      offer.issuer === address
    )
      return setTraitFilterCount(null);

    const { data: filterCount } = await axios.get(
      `${constants.api.url_new}/collection/availableNftsForTraitBid`,
      {
        params: {
          signature: offer.signature,
          walletAddress: address,
        },
      },
    );
    setTraitFilterCount(filterCount.data);
  };

  useEffect(() => {
    refreshTraitFilterCount();
  }, [offer]);
  const { id, end, issuer } = offer;

  const price = normalizeEther(offer?.price);

  const { data, isLoading } = useGetItemsByCollectionAddress(
    offer?.collectionAddress,
    {
      itemAttributes: offer.traitFilter,
      owner: address,
    },
    10,
    1,
    false,
  );
  const nfts = data?.results;

  const show2Col = offer?.traitFilter
    ? Object.keys(offer?.traitFilter).some((traitKey) => {
        const traitValues = offer?.traitFilter[traitKey];
        return traitValues.length > 1;
      })
    : false;

  let offerStatus = '';
  if (offer?.status === 'canceled') {
    offerStatus = 'Canceled';
  } else if (offer?.isExpired) {
    offerStatus = 'Expired';
  } else {
    offerStatus = 'Expire: ' + getEnd(end);
  }

  let additionalTooltipProps = {};
  if (isMobile) {
    additionalTooltipProps.onClick = () => setIsTooltipOpen(!isTooltipOpen);
  }

  let content = null;

  if (isConnected && (!offer?.isReady || isLoading)) {
    content = (
      <div className='alert-info rounded-lg p-3 text-center text-sm'>
        <span className='font-semibold'>Loading...</span>
      </div>
    );
  } else if (offer?.rarityFilter && Object.keys(offer?.rarityFilter).length) {
    content = (
      <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
        <Tooltip
          title={
            <div className='max-h-[320px] space-x-1 space-y-4 overflow-y-auto'>
              {offer.rarityFilter.min} - {offer.rarityFilter.max}
            </div>
          }
          classes={{
            tooltip:
              '!text-black !w-full !min-w-max !rounded-lg !bg-jacarta-50 !p-3 !text-center dark:!bg-jacarta-800 dark:!text-white dark:!border-jacarta-500 !border-jacarta-100 !border',
          }}
          enterTouchDelay={0}
          placement='top'
          disableFocusListener
          disableTouchListener
          open={isTooltipOpen}
          {...(isMobile
            ? {
                onClick: () => setIsTooltipOpen(!isTooltipOpen),
              }
            : {})}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <div className='mt-4 flex items-center space-x-1'>
            <span className='cursor-pointer font-semibold text-accent-dark dark:text-accent'>
              See Rarity Range
            </span>
          </div>
        </Tooltip>
      </ClickAwayListener>
    );
  } else if (offer?.traitFilter && Object.keys(offer?.traitFilter).length) {
    content = (
      <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
        <Tooltip
          title={
            <div className='max-h-[320px] space-x-1 space-y-4 overflow-y-auto'>
              {Object.keys(offer?.traitFilter).map((traitKey, i) => {
                const traitValues = offer?.traitFilter[traitKey];
                return (
                  <div key={i} className='min-w-max space-y-2 rounded-lg'>
                    <span className='text-sm font-semibold capitalize'>{traitKey}</span>
                    <div
                      className={`grid ${
                        show2Col ? 'grid-cols-2' : 'grid-cols-1'
                      } gap-2 text-center text-sm`}
                    >
                      {traitValues.map((traitValue, j) => {
                        return (
                          <div
                            key={j + traitValue}
                            className={
                              'text-black flex h-fit w-36 items-center justify-center rounded-lg border' +
                              ' border-jacarta-100 bg-jacarta-100 dark:border-jacarta-500 dark:bg-jacarta-700 dark:text-white' +
                              ' px-2 py-2'
                            }
                          >
                            <span className='text-sm font-medium'>{traitValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          }
          classes={{
            tooltip:
              '!text-black !w-full !min-w-max !rounded-lg !bg-jacarta-50 !p-3 !text-center dark:!bg-jacarta-800 dark:!text-white dark:!border-jacarta-500 !border-jacarta-100 !border',
          }}
          enterTouchDelay={0}
          placement='top'
          disableFocusListener
          disableTouchListener
          open={isTooltipOpen}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
          {...additionalTooltipProps}
        >
          <div className='mt-4 flex items-center space-x-1'>
            <span className='cursor-pointer font-semibold text-accent-dark dark:text-accent'>
              See Traits
            </span>
          </div>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  return (
    <div
      className='overflow-hidden rounded-2lg border border-jacarta-100 bg-white dark:border-jacarta-700 dark:bg-jacarta-700'
      key={id}
    >
      <ImageFixedAO
        image={getCdnUrl(offer?.fromUser?.profilePhoto ?? collection?.profilePhotoPath)}
        alt={`${offer?.id}}`}
      />
      <div className='flex flex-col items-center p-3 py-4'>
        <div className='text-md flex items-center space-x-1 font-bold'>
          <ChainIcon name={offer?.chain} tooltip={getTokenName(offer?.chain)} sideGap />
          <span>{normalizeEther(price)}</span>
        </div>

        <div className='mt-4'>
          <div className='text-center'>
            Offer: {offer?.filledGlobalBids}/{offer?.globalBidAmount} filled
          </div>
          <div className='text-center font-display'>{offerStatus}</div>
        </div>
        <div className='mt-4'>USD: ~ ${normalizeNumber(usdPrice * price, 3)}</div>

        <div className='mt-2 flex items-center space-x-1'>
          <span>From: </span>
          <ShowUser user={offer?.fromUser} address={issuer} textSm={false} hideImage showName />
        </div>

        <div className='mt-4 flex w-full flex-col flex-wrap items-center justify-center space-y-3'>
          {content}
          {typeof offer?.hasEnoughBalance === 'boolean' &&
          !offer?.hasEnoughBalance &&
          // !wrongNetwork &&
          isConnected &&
          !['canceled', 'filled'].includes(offer?.status) ? (
            <div className='alert-info rounded-lg p-3 text-center text-sm'>
              <span className='font-semibold'>
                The bidder has insufficient {getCoinGeckoWrappedTokenId(offer?.chain).toUpperCase()}{' '}
                balance.{' '}
              </span>
              Conversion from {getTokenName(offer?.chain)} to{' '}
              {getCoinGeckoWrappedTokenId(offer?.chain).toUpperCase()} is done during the offer and
              if the {getCoinGeckoWrappedTokenId(offer?.chain).toUpperCase()} balance falls below
              the offered value, the offer cannot be accepted.
            </div>
          ) : null}
          {isConnected &&
            !offer?.isApprovedEnough &&
            offer?.hasEnoughBalance &&
            signatureStillActive && (
              <div className='w-full items-center justify-center'>
                <div className='alert-error rounded-lg p-3 text-center text-sm'>
                  <span className='font-semibold'>
                    {/* The bidder has not approved enough{' '} */}
                    {isConnected && (
                      <div>
                        <span>
                          {issuer === address
                            ? 'You have not approved enough '
                            : 'The bidder has not approved enough '}
                          {getCoinGeckoWrappedTokenId(offer?.chain).toUpperCase()} for this offer.
                          {` `}
                          {issuer === address &&
                            'You must set your approval as at least your bid amount.'}
                        </span>
                        {issuer === address && (
                          <>
                            <br />
                            <a
                              href='https://support.metamask.io/hc/en-us/articles/6174898326683-What-is-a-token-approval-'
                              className='mt-2 flex items-center justify-center text-accent'
                              rel='noreferrer'
                              target='_blank'
                            >
                              <span style={{ fontSize: '15px' }}>Learn More</span>
                              <FaExternalLinkAlt className='ml-1 inline-block' />
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </span>
                </div>
              </div>
            )}
          {isConnected &&
            !offer?.isApprovedEnough &&
            offer?.hasEnoughBalance &&
            signatureStillActive &&
            issuer === address && (
              <button
                className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
                data-testid='approve'
                onClick={async () => {
                  await approveERC20(tokenContract, address, offer?.price, chain?.marketplace);
                }}
                style={{
                  fontSize: '14px',
                }}
              >
                Approve {getCoinGeckoWrappedTokenId(chain.hexId).toUpperCase()}
              </button>
            )}
          {isConnected &&
            issuer !== address &&
            offer?.hasEnoughBalance &&
            signatureStillActive &&
            offer?.isApprovedEnough &&
            !['canceled', 'filled'].includes(offer?.status) &&
            // !wrongNetwork &&
            nfts?.length > 0 && (
              <button
                className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
                data-testid='acceptBidModal'
                onClick={() =>
                  dispatch(
                    setModal({
                      key: 'acceptBidModal',
                      data: {
                        signature: offer?.signature,
                        kind: 0,
                        chain: offer?.chain,
                        offer,
                        userNfts: nfts,
                        collectionInfo: collection,
                      },
                    }),
                  )
                }
                style={{
                  fontSize: '14px',
                }}
              >
                Accept Offer{' '}
                {offer.traitFilter &&
                  traitFilterCount &&
                  `(${traitFilterCount} NFT${traitFilterCount > 1 ? 's' : ''})`}
              </button>
            )}
          {isConnected &&
            issuer === address &&
            // !wrongNetwork &&
            !['canceled', 'filled'].includes(offer?.status) && (
              <button
                className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
                style={{
                  fontSize: '14px',
                }}
                data-testid='cancelModal'
                onClick={() =>
                  dispatch(
                    setModal({
                      key: 'cancelModal',
                      data: {
                        id: id,
                        signature: offer?.signature,
                        isGlobal: true,
                        kind: offer?.kind,
                        chain: offer?.chain,
                        other: offer,
                        collection: collection,
                      },
                    }),
                  )
                }
              >
                Cancel Offer
              </button>
            )}
          {!isConnected && (
            <div className='w-full'>
              <ConnectButtonWrapper>
                <div className='mt-2 w-full'>
                  <button className='rounded-2xl hover:bg-accent-hover flex w-full items-center justify-center border border-transparent bg-accent px-5 py-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2'>
                    Connect Wallet
                  </button>
                </div>
              </ConnectButtonWrapper>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

GlobalOfferItem.propTypes = {
  offer: PropTypes.shape(offerShape),
};

export default GlobalOfferItem;
